import { CheckboxGroup } from '@s-group/design-system-components/CheckboxGroup/CheckboxGroup'
import { assertValue } from '@shared/helpers'
import { IndentedFormFieldContainer } from '@ui-components/global/FormStyling'
import classNames from 'classnames'
import { ReactNode } from 'react'
import { styled } from 'styled-components'

import { ErrorAndDescriptionRow } from '../common/ErrorAndDescriptionRow'
import { Label } from '../dataDisplay/Label'
import { VisuallyHidden } from '../decorative/VisuallyHidden'
import { Colors } from '../global/colors'
import { PaddedDiv } from '../global/CommonStyling'
import { FormGroupOption, FormGroupProps } from '../global/ComponentProps'
import { ROUNDING_DEFAULT } from '../global/constants'
import { buildAriaAttributes } from '../global/global'
import { SPACE_XXSMALL, SPACE_XXXXSMALL } from '../global/spacing'
import { parseComponentId, removeKeysFromObject } from '../utilities/helpers'
import { FormLabelledCheckbox } from './FormLabelledCheckbox'

const BorderedPaddedDiv = styled(PaddedDiv)<{ $hasLabel?: boolean }>`
  margin-top: ${({ $hasLabel }) => ($hasLabel ? 3 : 1)}px;
  border-radius: 8px;
  padding: 0;
  border: 1px solid ${Colors.BorderWeakNeutral};
  &.error {
    border: 1px solid ${Colors.BorderError};
  }

  > {
    :first-child {
      border-top-left-radius: ${ROUNDING_DEFAULT}px;
      border-top-right-radius: ${ROUNDING_DEFAULT}px;
    }
    :last-child {
      border-bottom-left-radius: ${ROUNDING_DEFAULT}px;
      border-bottom-right-radius: ${ROUNDING_DEFAULT}px;
    }
    :hover:not(.disabled) {
      background-color: ${Colors.BackgroundWeakNeutral};
    }

    > {
      :first-child {
        border-bottom: 0px;
        border-bottom-left-radius: 0px;
        border-bottom-right-radius: 0px;
      }
      :last-child {
        border-bottom-left-radius: 0px;
        border-bottom-right-radius: 0px;
      }
    }

    label {
      cursor: pointer;

      > div.focus {
        border: 2px solid #4d74fe;
        margin: -2px;
      }
    }
  }

  > {
    :not(:last-child) {
      border-bottom: 1px solid ${Colors.BorderWeakNeutral};
    }
  }
`

const StyledCheckboxGroup = styled(CheckboxGroup)<{ $hasLabel?: boolean }>`
  ${({ $hasLabel }) => !$hasLabel && `padding: 0 0 ${SPACE_XXSMALL} 0`};
  && {
    gap: 0;
  }
`

const StyledErrorAndDescriptionRow = styled(ErrorAndDescriptionRow)`
  margin-top: ${SPACE_XXXXSMALL};
`

export const FormCheckboxGroup = (props: Omit<FormGroupProps, 'border'>): ReactNode => {
  const {
    id,
    name,
    required,
    options,
    side = 'left',
    optionsDisabledStates = [],
    onBlur: originalOnBlur,
    error,
    label,
    hiddenLabel,
  } = props
  const hasLabel = !!label

  const { children, ...withoutChildren } = props

  const overriddenId = parseComponentId(name, id)
  const overridesHandledByGroup = { required: false, border: false, error: undefined }
  const hasError = !!error
  const hasDescription = !!props.description

  const overriddenOnBlur = async (event: React.FocusEvent<HTMLInputElement, HTMLInputElement>) => {
    const { relatedTarget, target } = event
    if (originalOnBlur && relatedTarget?.name !== target?.name) {
      originalOnBlur(event)
    }
  }

  const checkboxGroup = options.map(function ({ key, label: checkBoxLabel, description }: FormGroupOption) {
    return (
      <FormLabelledCheckbox
        {...withoutChildren}
        id={`${overriddenId}-option-${key}`}
        label={checkBoxLabel}
        key={`${overriddenId}-option-${key}`}
        value={key}
        onBlur={overriddenOnBlur}
        disabled={!!optionsDisabledStates.includes(key)}
        $shouldCenterItemsVertically={true}
        $isGrouped={true}
        groupId={overriddenId}
        {...overridesHandledByGroup}
        description={description}
      />
    )
  })

  const ariaAttributes = removeKeysFromObject(
    'aria-required',
    buildAriaAttributes(overriddenId, false, hasDescription, hasError),
  )

  return (
    <div>
      <StyledCheckboxGroup data-testid={`fieldset-${overriddenId}`} {...ariaAttributes} $hasLabel={hasLabel}>
        {hasLabel ? (
          <Label as="legend" isRequired={required} label={label} id={overriddenId} description={props.description} />
        ) : (
          assertValue(hiddenLabel, 'hiddenLabel') && (
            <VisuallyHidden id={`${overriddenId}-label`}>{hiddenLabel}</VisuallyHidden>
          )
        )}
        <BorderedPaddedDiv className={classNames({ error: hasError, right: side === 'right' })} $hasLabel={hasLabel}>
          {checkboxGroup}
        </BorderedPaddedDiv>
        <StyledErrorAndDescriptionRow error={error} id={overriddenId} />
      </StyledCheckboxGroup>
      {children && <IndentedFormFieldContainer>{children}</IndentedFormFieldContainer>}
    </div>
  )
}
